import { Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseCancelDevicePowerOffActionProps = {
    deviceId: string;
};

type UseCancelDevicePowerOffActionData = {
    cancelDevicePowerOff: () => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Withdrawing a power off the device has not picked up yet.
 *
 * No `isAllowed`/`reason`: the gate is the device settings page's own
 * `canEdit`, which it works out once for every action on the device and passes
 * down to the buttons.
 */
export function useCancelDevicePowerOffAction(
    props: UseCancelDevicePowerOffActionProps
): UseCancelDevicePowerOffActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `DELETE /v3/devices/{deviceId}/actions/power-off` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.devices
                .cancelDevicePowerOff(deviceId)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            // What comes back no longer carries the pending change, and the
            // lists have to stop showing one.
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.cancel_device_action.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        cancelDevicePowerOff: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
